import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { SessionGuard } from './core/guards/session.guard';
import { OnboardingDataGuard } from './core/guards/onboarding-data.guard';
import { USER_TYPE } from './core/enums/user';
import { RoleGuard } from './core/guards/role.guard';
import {
  homeSchema,
  aboutUsSchema,
  tinRegistrationSchema,
  vatFilingSchema,
  taxClearanceSchema,
} from './core/constants/structured-data.constants';
import { PROFILE_ROUTES } from './projects/clients/modules/profile/profile.routes';

export const routes: Routes = [
  {
    path: 'auth',
    children: [
      {
        path: 'sign-in',
        loadComponent: () =>
          import('./auth/components/sign-in/sign-in.component').then((c) => c.SignInComponent),
        canActivate: [SessionGuard],
      },

      {
        path: 'register',
        loadComponent: () =>
          import('./auth/components/register/sign-up.component').then((c) => c.SignUpComponent),
        canActivate: [SessionGuard],
      },

      {
        path: 'sign-up',
        loadComponent: () =>
          import('./auth/components/sign-up/sign-up.component').then((c) => c.SignUpComponent),
        canActivate: [SessionGuard],
      },
      {
        canActivate: [SessionGuard],
        path: 'verify',
        loadComponent: () =>
          import('./auth/components/email-verification/email-verification.component').then(
            (c) => c.EmailVerificationComponent,
          ),
      },
      {
        path: 'forget-password',
        loadComponent: () =>
          import('./auth/components/forgot-password/forgot-password.component').then(
            (c) => c.ForgotPasswordComponent,
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./auth/components/reset-password/reset-password.component').then(
            (c) => c.ResetPasswordComponent,
          ),
      },
      {
        path: 'verify-email-password',
        loadComponent: () =>
          import('./auth/components/verify-email-password/verify-email-password.component').then(
            (c) => c.VerifyEmailPasswordComponent,
          ),
      },
      {
        path: 'oauth-redirect',
        loadComponent: () =>
          import('./auth/components/oauth2-redirect/oauth2-redirect.component').then(
            (c) => c.OAuth2RedirectComponent,
          ),
      },
    ],
  },
  {
    path: 'tenant',
    loadComponent: () => import('./projects/tenants/tenants').then((m) => m.Tenants),
    // children: [{}]
  },
  {
    path: 'admin-portal',
    loadComponent: () => import('./projects/admin-portal/admin-portal').then((m) => m.AdminPortal),
    canActivate: [RoleGuard],
    data: { hideBreadcrumb: false, roles: [USER_TYPE.SUPER_ADMIN] },
    children: [
      {
        path: '',
        redirectTo: 'partners',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import(
            './projects/admin-portal/components/placeholder-module/placeholder-module.component'
          ).then((m) => m.AdminPlaceholderModuleComponent),
        data: {
          title: 'Dashboard',
          description:
            'Get a quick overview of campaign performance, partner activity, and operational health.',
        },
      },
      {
        path: 'partners',
        loadComponent: () =>
          import(
            './projects/admin-portal/components/partners-module/partners-module.component'
          ).then((m) => m.AdminPartnersModuleComponent),
        data: {
          title: 'Partner Directory',
          description:
            'Launch and manage partner profiles, relationships, and onboarding workflows.',
        },
      },
      {
        path: 'partners/:id',
        loadComponent: () =>
          import(
            './projects/admin-portal/components/partner-details/partner-details.component'
          ).then((m) => m.AdminPartnerDetailsComponent),
        data: {
          title: 'Partner Details',
          description:
            'View partner information, create users for the partner, and manage assigned users.',
        },
      },
      {
        path: 'discounts',
        loadComponent: () =>
          import(
            './projects/admin-portal/components/discount-module/discount-module.component'
          ).then((m) => m.AdminDiscountModuleComponent),
        data: {
          title: 'Generate Discount Code',
          description:
            'Create custom discount codes for your customers and monitor recent campaign performance.',
        },
      },
      {
        path: 'referrals',
        loadComponent: () =>
          import(
            './projects/admin-portal/components/referral-module/referral-module.component'
          ).then((m) => m.AdminReferralModuleComponent),
        data: {
          title: 'Referral Modules',
          description:
            'Manage referral campaigns, partner rewards, and conversion-focused sharing programs.',
        },
      },
      {
        path: 'analytics',
        loadComponent: () =>
          import(
            './projects/admin-portal/components/placeholder-module/placeholder-module.component'
          ).then((m) => m.AdminPlaceholderModuleComponent),
        data: {
          title: 'Analytics',
          description:
            'Review funnel trends, conversion data, and campaign results across the admin workspace.',
        },
      },
      {
        path: 'settings',
        loadComponent: () =>
          import(
            './projects/admin-portal/components/placeholder-module/placeholder-module.component'
          ).then((m) => m.AdminPlaceholderModuleComponent),
        data: {
          title: 'Settings',
          description: 'Configure admin preferences, publishing rules, and operational controls.',
        },
      },
    ],
  },
  {
    path: 'about-us',
    loadComponent: () => import('./home-pages/about-us/about-us').then((m) => m.AboutUs),
    data: {
      title: 'About ComplyDesk',
      description:
        'Learn about ComplyDesk’s mission to simplify tax compliance, filing, and reporting for Nigerian businesses.',
      keywords:
        'Tax, Compliance, Tax Compliance, tax sofware, e-invoicing, about ComplyDesk, tax compliance software, Nigerian tax solutions, company information, financial services',
      robots: 'index, follow',
      structuredData: aboutUsSchema,
    },
  },

  {
    path: 'features',
    loadComponent: () => import('./home-pages/features/features').then((m) => m.Features),
    data: {
      title: 'Features | ComplyDesk',
      description:
        'Explore ComplyDesk features for tax filing, TIN registration, VAT compliance, and payment tracking.',
      keywords:
        'tax compliance features, VAT filing, TIN application, tax dashboard, document upload',
      robots: 'index, follow',
    },
  },

  {
    path: 'terms-of-service',
    loadComponent: () =>
      import('./home-pages/terms-of-service/terms-of-service').then((m) => m.TermsOfService),
    data: {
      title: 'Terms of Service | ComplyDesk',
      description:
        'Read the ComplyDesk terms of service to understand usage, liability, and platform policies.',
      keywords: 'terms of service, complydesk terms, user agreement, site policy',
      robots: 'index, follow',
    },
  },

  {
    path: 'contact-us',
    loadComponent: () => import('./home-pages/contact-us/contact-us').then((m) => m.ContactUs),
    data: {
      title: 'Contact Us | ComplyDesk',
      description:
        'Get in touch with ComplyDesk support to ask about tax compliance services, onboarding, or account help.',
      keywords: 'contact ComplyDesk, tax support, Nigerian tax software contact',
      robots: 'index, follow',
    },
  },

  {
    path: 'compliance-check',
    loadComponent: () =>
      import('./home-pages/compliance-check/compliance-check').then((m) => m.ComplianceCheck),
    data: {
      title: 'Free Business Compliance Check | ComplyDesk',
      description:
        'Take the free 3-minute ComplyDesk Business Compliance Check to see which tax and compliance areas of your Nigerian business may need attention.',
      keywords:
        'free compliance check, business compliance Nigeria, tax compliance check, VAT PAYE WHT compliance, CAC registration',
      robots: 'index, follow',
    },
  },

  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./home-pages/privacy-policy/privacy-policy').then((m) => m.PrivacyPolicy),
    data: {
      title: 'Privacy Policy | ComplyDesk',
      description:
        'Review how ComplyDesk protects data, manages privacy, and secures customer tax information.',
      keywords: 'privacy policy, data protection, ComplyDesk privacy, GDPR compliance',
      robots: 'index, follow',
    },
  },

  {
    path: 'clients',
    loadComponent: () => import('./projects/clients/clients').then((m) => m.Clients),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      roles: [USER_TYPE.CLIENTS],
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./projects/clients/modules/layout/dashboard-layout').then(
            (m) => m.DashboardLayout,
          ),
        data: {
          breadcrumb: 'Dashboard',
          breadcrumbIcon: 'fa-solid fa-home',
          breadcrumbUrl: '/clients/dashboard',
        },
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./projects/clients/modules/dashboard/dashboard').then((m) => m.Dashboard),
            data: { hideBreadcrumb: true },
          },
          {
            path: 'upload',
            loadComponent: () =>
              import('./projects/clients/modules/upload/upload').then((m) => m.Upload),
            data: { breadcrumb: 'Upload' },
          },
          {
            path: 'status',
            loadComponent: () =>
              import('./projects/clients/modules/status/status').then((m) => m.Status),
            data: { breadcrumb: 'Status' },
          },
          {
            path: 'deadlines',
            loadComponent: () =>
              import('./projects/clients/modules/deadlines/deadlines').then((m) => m.Deadlines),
            data: { breadcrumb: 'Deadlines' },
          },
          {
            path: 'orders/:orderId',
            loadComponent: () =>
              import('./projects/clients/modules/order-detail/order-detail').then(
                (m) => m.OrderDetail,
              ),
            data: { breadcrumb: 'Order Details' },
          },
          // Old per-tax order pages: one order page now serves every service.
          { path: 'vat-orders/:orderId', redirectTo: 'orders/:orderId', pathMatch: 'full' },
          { path: 'wht-orders/:orderId', redirectTo: 'orders/:orderId', pathMatch: 'full' },
          {
            path: 'tin-application',
            loadComponent: () => import('./projects/clients/modules/tin/tin').then((m) => m.Tin),
            data: { breadcrumb: 'TIN Application' },
          },
          {
            path: 'payment/services',
            loadComponent: () =>
              import('./projects/clients/modules/payment/service-selection/service-selection').then(
                (m) => m.ServiceSelection,
              ),
            data: { breadcrumb: 'Service Selection' },
          },
          {
            path: 'vat-filing',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/service-selection/components/client-vat-form/client-vat-form.component'
              ).then((m) => m.ClientVatFormComponent),
            data: { breadcrumb: 'VAT Filing' },
          },
          {
            path: 'wht-filing',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/service-selection/components/withholding-tax-form/withholding-tax-form.component'
              ).then((m) => m.WithholdingTaxFormComponent),
            data: { breadcrumb: 'WHT Filing' },
          },
          {
            path: 'tax-clearance',
            data: { breadcrumb: 'Tax Clearance Certificate' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './projects/clients/modules/payment/service-selection/components/tax-clearance-redirect/tax-clearance-redirect.component'
                  ).then((m) => m.TaxClearanceRedirectComponent),
              },
              {
                path: 'company',
                loadComponent: () =>
                  import(
                    './projects/clients/modules/payment/service-selection/components/company-tax-clearance-cert-form/company-tax-clearance-cert-form.component'
                  ).then((m) => m.CompanyTaxClearanceCertFormComponent),
              },
              {
                path: 'individual',
                loadComponent: () =>
                  import(
                    './projects/clients/modules/payment/service-selection/components/tax-clearance-form/tax-clearance-form.component'
                  ).then((m) => m.TaxClearanceFormComponent),
              },
            ],
          },
          {
            path: 'corporate-filing',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/service-selection/components/corporate-filing-form/corporate-filing-form.component'
              ).then((m) => m.CorporateFilingFormComponent),
            data: { breadcrumb: 'Corporate Filing' },
          },
          {
            path: 'individual-tax-filing',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/service-selection/components/individual-tax-form/individual-tax-form.component'
              ).then((m) => m.IndividualTaxFormComponent),
            data: { breadcrumb: 'Individual Tax Filing' },
          },
          {
            path: 'payment-details',
            loadComponent: () =>
              import('./projects/clients/modules/payment/payment-details/payment-details').then(
                (m) => m.PaymentDetails,
              ),
            data: { breadcrumb: 'Tax Filings' },
          },

          {
            path: 'tax-payment/callback',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/tax-payment-verification/tax-payment-verification'
              ).then((m) => m.TaxPaymentVerification),
            data: { breadcrumb: 'Payment Verification' },
          },
          {
            path: 'payment/callback',
            loadComponent: () =>
              import('./projects/clients/modules/payment-verifications/payment-verifications').then(
                (m) => m.PaymentVerifications,
              ),
          },
          {
            path: 'profile',
            loadComponent: () =>
              import('./projects/clients/modules/profile/profile-shell/profile-shell').then(
                (m) => m.ProfileShellComponent,
              ),
            data: { breadcrumb: 'Profile' },
            children: PROFILE_ROUTES,
          },
        ],
      },
      {
        path: 'company-onboarding',
        loadComponent: () =>
          import('./projects/clients/modules/onboarding/company/company-onboarding').then(
            (m) => m.CompanyOnboarding,
          ),
        children: [
          {
            path: 'company-info',
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/company/steps/company-info/company-info.component'
              ).then((m) => m.CompanyInfoComponent),
          },
          {
            path: 'business-details',
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/company/steps/business-details/business-details.component'
              ).then((m) => m.BusinessDetailsComponent),
          },
          {
            path: 'contacts-directors',
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/company/steps/contacts-and-directors/contacts-and-directors.component'
              ).then((m) => m.ContactsAndDirectorsComponent),
          },
          {
            path: 'verification',
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/company/steps/verification/verification.component'
              ).then((m) => m.VerificationComponent),
          },
          {
            path: 'complete-setup',
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/company/steps/complete-setup/complete-setup.component'
              ).then((m) => m.CompleteSetupComponent),
          },
          { path: '', redirectTo: 'company-info', pathMatch: 'full' },
        ],
      },
      {
        path: 'individual-onboarding',
        loadComponent: () =>
          import('./projects/clients/modules/onboarding/individual/individual').then(
            (m) => m.Individual,
          ),
        children: [
          {
            path: 'account-type',
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/individual/components/account-type-selection/account-type-selection'
              ).then((m) => m.AccountTypeSelectionComponent),
          },
          {
            path: 'employee-details',
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/individual/components/employee-details/employee-details'
              ).then((m) => m.EmployeeDetailsComponent),
          },
          {
            path: 'contact-info',
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/individual/components/contact-info/contact-info'
              ).then((m) => m.ContactInfoComponent),
          },
          {
            path: 'employment-income',
            canActivate: [OnboardingDataGuard],
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/individual/components/employment-income/employment-income'
              ).then((m) => m.EmploymentIncomeComponent),
          },
          {
            path: 'tax-assessment',
            canActivate: [OnboardingDataGuard],
            loadComponent: () =>
              import(
                './projects/clients/modules/onboarding/individual/components/tax-assessment/tax-assessment'
              ).then((m) => m.TaxAssessment),
          },
          { path: '', redirectTo: 'account-type', pathMatch: 'full' },
        ],
      },
      {
        path: 'user-selection',
        loadComponent: () =>
          import(
            './projects/clients/modules/onboarding/onboarding-user-selection/onboarding-user-selection'
          ).then((m) => m.OnboardingUserSelection),
      },
      {
        path: 'onboarding-success',
        loadComponent: () =>
          import(
            './projects/clients/modules/onboarding/onboarding-success/onboarding-success'
          ).then((m) => m.OnboardingSuccess),
      },
      {
        path: 'review-submit',
        canActivate: [OnboardingDataGuard],
        loadComponent: () =>
          import(
            './projects/clients/modules/onboarding/individual/components/review-submit/review-submit'
          ).then((m) => m.ReviewSubmitComponent),
      },
      {
        path: 'business-selection',
        loadComponent: () =>
          import(
            './projects/clients/modules/onboarding/individual/components/business-selection/business-selection.component'
          ).then((m) => m.BusinessSelectionComponent),
      },
      {
        path: 'service-selection',
        loadComponent: () =>
          import('./projects/clients/modules/service-selection/service-selection').then(
            (m) => m.ServiceSelection,
          ),
        data: { breadcrumb: 'Service Selection', breadcrumbIcon: 'fa-solid fa-list-check' },
      },
      {
        path: 'order-summary',
        loadComponent: () =>
          import('./projects/clients/modules/order-summary/order-summary').then(
            (m) => m.OrderSummary,
          ),
        data: { breadcrumb: 'Order Summary', breadcrumbIcon: 'fa-solid fa-file-invoice' },
      },

      {
        path: 'payment-method',
        loadComponent: () =>
          import('./projects/clients/modules/payment-method/payment-method').then(
            (m) => m.PaymentMethod,
          ),
      },

      {
        path: 'tax-payment-method',
        loadComponent: () =>
          import('./projects/clients/modules/payment/tax-payment-method/tax-payment-method').then(
            (m) => m.TaxPaymentMethod,
          ),
        data: { hideBreadcrumb: true },
      },
      {
        path: '',
        redirectTo: 'individual-onboarding',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'tax-managers',
    canActivate: [AuthGuard],
    data: {
      roles: [USER_TYPE.TAX_PREPARER, USER_TYPE.ACCOUNTANT],
    },
    loadComponent: () => import('./projects/tax-managers/tax-managers').then((m) => m.TaxManagers),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./projects/tax-managers/modules/layout/dashboard-layout').then(
            (m) => m.DashboardLayout,
          ),
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            data: { breadcrumb: 'Dashboard', breadcrumbIcon: 'fa-solid fa-house' },
            children: [
              {
                path: '',
                canActivate: [RoleGuard],
                loadComponent: () =>
                  import('./projects/tax-managers/modules/dashboard/dashboard').then(
                    (m) => m.Dashboard,
                  ),
                data: { hideBreadcrumb: true, roles: [USER_TYPE.TAX_PREPARER] },
              },
              {
                path: 'supervisor',
                canActivate: [RoleGuard],
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/supervisor/supervisor'
                  ).then((m) => m.Supervisor),
                data: { roles: [USER_TYPE.TENANT_ADMIN] },
              },
              {
                path: 'client/:clientId',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/client-details/client-details'
                  ).then((m) => m.ClientDetails),
                data: { breadcrumb: 'Client Details' },
              },
              {
                path: 'orders/:orderId',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/order-detail/order-detail'
                  ).then((m) => m.TaxManagerOrderDetail),
                data: { breadcrumb: 'Order Details' },
              },
              {
                path: 'vat-orders/:orderId',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/vat-order-detail/vat-order-detail'
                  ).then((m) => m.TaxManagerVatOrderDetail),
                data: { breadcrumb: 'VAT Order Details', filingType: 'VAT' },
              },
              {
                path: 'wht-orders/:orderId',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/vat-order-detail/vat-order-detail'
                  ).then((m) => m.TaxManagerVatOrderDetail),
                data: { breadcrumb: 'WHT Order Details', filingType: 'WHT' },
              },
              {
                path: 'wht-orders/:orderId/wht-form',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/withholding-tax/withholding-tax.component'
                  ).then((m) => m.WithholdingTaxComponent),
                data: { breadcrumb: 'WHT Return Form' },
              },
              {
                path: 'vat-orders/:orderId/vat-form',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/vat-form/vat-form.component'
                  ).then((m) => m.VatFormComponent),
                data: { breadcrumb: 'VAT Return Form' },
              },
              {
                path: 'bank-transfer-review',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/bank-transfer-review/bank-transfer-review'
                  ).then((m) => m.BankTransferReview),
                data: { breadcrumb: 'Bank Transfer Review' },
              },
              {
                path: 'accountant',
                canActivate: [RoleGuard],
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/accountant-dashboard/accountant-dashboard'
                  ).then((m) => m.AccountantDashboard),
                data: { breadcrumb: 'CA Review Dashboard', roles: [USER_TYPE.ACCOUNTANT] },
              },
              {
                path: 'review/:taskId',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/review-page/review-page'
                  ).then((m) => m.ReviewPage),
                data: { breadcrumb: 'Review Task' },
              },

              {
                path: 'cit',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/company-income-tax/cit'
                  ).then((m) => m.CITComponent),
                data: { breadcrumb: 'Company Income Tax' },
              },

              {
                path: 'manage-users',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/manage-users/manage-users'
                  ).then((m) => m.ManageUsersComponent),
                data: { breadcrumb: 'Manage Users' },
              },
              {
                path: 'turnover-verification/:id',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/turnover-verification/turnover-verification'
                  ).then((m) => m.TurnoverVerification),
                data: { breadcrumb: 'Turnover Verification' },
              },
              {
                path: 'order-turnover-verification/:id',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/order-turnover-verification/order-turnover-verification'
                  ).then((m) => m.OrderTurnoverVerification),
                data: { breadcrumb: 'Order Turnover Verification' },
              },
              {
                path: 'discounts',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/discounts/discounts'
                  ).then((m) => m.DiscountsComponent),
                data: { breadcrumb: 'Discount Management' },
              },
            ],
          },
          {
            path: 'dashboard/firs-filing',
            loadComponent: () =>
              import(
                './projects/tax-managers/modules/dashboard/components/firs-filing-form/firs-filing-form.component'
              ).then((m) => m.FirsFilingFormComponent),
            data: { breadcrumb: 'FIRS Filing Form' },
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import('./projects/tax-managers/modules/notifications/notifications').then(
                (m) => m.TaxManagerNotifications,
              ),
            data: { breadcrumb: 'Notifications' },
          },
          {
            path: 'referral',
            loadComponent: () =>
              import('./projects/tax-managers/modules/dashboard/components/referral/referral').then(
                (m) => m.Referral,
              ),
            data: { breadcrumb: 'Referral' },
          },
        ],
      },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./home-pages/landing-page/landing-page').then((m) => m.LandingPage),
    data: {
      title: 'ComplyDesk | Simplified Tax Compliance',
      description:
        'ComplyDesk helps Nigerian businesses manage tax filing, TIN registration, VAT compliance, and payment workflows in one platform.',
      keywords:
        'tax, tax compliance, Tax compliance nigeria, Tax solutions in Nigeria, Tax in Africa, tax in nigeria, inventory management, e-invoicing,  TIN application, VAT filing, Nigerian tax software, tax filing platform',
      robots: 'index, follow',
      structuredData: homeSchema,
    },
  },

  {
    path: 'tin-registration-nigeria',
    loadComponent: () =>
      import('./home-pages/tin-registration-nigeria/tin-registration-nigeria').then(
        (m) => m.TinRegistrationNigeria,
      ),
    data: {
      title: 'TIN Registration in Nigeria',
      description:
        'Register for a Tax Identification Number (TIN) in Nigeria with step-by-step guidance, document upload, and dedicated chartered accountant support from ComplyDesk.',
      keywords:
        'TIN registration Nigeria, how to register TIN Nigeria, Tax Identification Number Nigeria, FIRS TIN application, TIN for company Nigeria, TIN for individual Nigeria',
      robots: 'index, follow',
      structuredData: tinRegistrationSchema,
    },
  },

  {
    path: 'vat-filing-nigeria',
    loadComponent: () =>
      import('./home-pages/vat-filing-nigeria/vat-filing-nigeria').then((m) => m.VatFilingNigeria),
    data: {
      title: 'VAT Filing & Compliance in Nigeria',
      description:
        'File and manage VAT returns in Nigeria with ComplyDesk — automated tax calculation, e-invoicing, deadline reminders, and chartered accountant support.',
      keywords:
        'VAT filing Nigeria, VAT compliance Nigeria, how to file VAT Nigeria, VAT return Nigeria, Nigerian VAT software, e-invoicing Nigeria',
      robots: 'index, follow',
      structuredData: vatFilingSchema,
    },
  },

  {
    path: 'tax-clearance-certificate-nigeria',
    loadComponent: () =>
      import(
        './home-pages/tax-clearance-certificate-nigeria/tax-clearance-certificate-nigeria'
      ).then((m) => m.TaxClearanceCertificateNigeria),
    data: {
      title: 'Tax Clearance Certificate (TCC) in Nigeria',
      description:
        'Apply for a Tax Clearance Certificate in Nigeria for your company or as an individual, with document upload and dedicated compliance support from ComplyDesk.',
      keywords:
        'tax clearance certificate Nigeria, TCC Nigeria, company tax clearance certificate, individual tax clearance certificate, apply for TCC Nigeria',
      robots: 'index, follow',
      structuredData: taxClearanceSchema,
    },
  },

  {
    path: 'reset-password',
    redirectTo: 'auth/reset-password',
    pathMatch: 'full',
  },
  {
    path: 'verify-email-password',
    redirectTo: 'auth/verify-email-password',
    pathMatch: 'full',
  },
  {
    path: 'payment/callback',
    redirectTo: '/clients/payment/callback',
    pathMatch: 'full',
  },
  {
    path: '**',
    loadComponent: () =>
      import('./core/components/not-found/not-found-wrapper.component').then(
        (m) => m.NotFoundWrapperComponent,
      ),
  },
];
